/**
 * Settings → My Account → My roles: which of your roles you feature.
 *
 * The one featured-roles control (user_rich_profiles.featured_job_role_slugs,
 * saved through userStore.saveFeaturedJobRoles so it follows the person to any
 * device). Two things read it: the role-based menu (buildRoleMenu — the short
 * side menu and the phone drawer) and the home's team questions (R-5). It was
 * Settings → Home until 2026-10-14. Only roles the person holds are offered.
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, UserCog } from 'lucide-react';
import { PageHeader } from '../../components/prioritization/PageHeader';
import { useUserStore } from '../../stores/prioritization/userStore';
import { jobSlugsFromAssignments, getJobRoleDefinition } from '../../lib/contributorNavProfiles';
import { useContributorExperience } from '../../hooks/prioritization/useContributorExperience';

export default function FeaturedRolesSettings() {
  const assignedRoleNames = useUserStore((s) => s.assignedRoleNames);
  const featuredJobRoleSlugs = useUserStore((s) => s.featuredJobRoleSlugs);
  const saveFeaturedJobRoles = useUserStore((s) => s.saveFeaturedJobRoles);
  const assignedJobSlugs = jobSlugsFromAssignments(assignedRoleNames ?? []);
  // Only people on the role-based menu have a menu that follows their roles;
  // an admin's full menu does not, so the copy does not promise it to them.
  const { useContributorShell } = useContributorExperience();
  const follows = useContributorShell ? 'Your menu and your home show' : 'Your home shows';

  const [featured, setFeatured] = useState<string[]>(featuredJobRoleSlugs ?? []);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);

  // Follow the saved value only when it really changes. The store hands out a
  // new (equal) array each time permissions reload, and resetting on that
  // threw away picks made before Save (seen on staging, 2026-10-14).
  const savedKey = (featuredJobRoleSlugs ?? []).join(',');
  useEffect(() => {
    setFeatured(savedKey ? savedKey.split(',') : []);
  }, [savedKey]);

  const toggle = (slug: string) => {
    setMessage(null);
    setFeatured((prev) => (prev.includes(slug) ? prev.filter((s) => s !== slug) : [...prev, slug]));
  };

  const save = async () => {
    setSaving(true);
    setMessage(null);
    const ok = await saveFeaturedJobRoles(featured);
    setSaving(false);
    setMessage(ok ? { kind: 'ok', text: `Saved. ${follows} these roles’ work.` } : { kind: 'error', text: 'Could not save your roles. Try again.' });
  };

  return (
    <div className="max-w-2xl">
      <div className="mb-4">
        <Link to="/prioritization/settings" className="inline-flex items-center gap-1 text-sm text-[var(--text-ter)] hover:text-[var(--text-sec)]">
          <ArrowLeft className="h-4 w-4" /> Settings
        </Link>
      </div>
      <PageHeader
        icon={<UserCog className="h-4 w-4 text-[var(--text-sec)]" />}
        iconBg="bg-[var(--hover-tint)]"
        title="My roles"
        subtitle={`Feature the roles you work in. ${follows} these roles’ work.`}
      />

      {assignedJobSlugs.length === 0 ? (
        <p className="text-base text-[var(--text-sec)]">
          You hold no named role in this workspace, so there is nothing to feature. Your home shows the questions about you and where the plan stands.
        </p>
      ) : (
        <section>
          <h2 className="text-sm font-semibold text-[var(--text-ter)] uppercase tracking-wider mb-3">Roles you hold</h2>
          <p className="text-base text-[var(--text-sec)] mb-4">
            Pick the roles to feature. Leave all unpicked to show the work of every role you hold.
          </p>
          <ul className="space-y-3 mb-4">
            {assignedJobSlugs.map((slug) => {
              const role = getJobRoleDefinition(slug);
              return (
                <li key={slug}>
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={featured.includes(slug)}
                      onChange={() => toggle(slug)}
                      className="mt-1 rounded border-[var(--rule)]"
                    />
                    <span>
                      <span className="text-base font-medium text-[var(--text-pri)]">{role?.displayName ?? slug}</span>
                      {role?.description && <span className="block text-sm text-[var(--text-ter)]">{role.description}</span>}
                    </span>
                  </label>
                </li>
              );
            })}
          </ul>
          {message && (
            <p className={`text-sm mb-3 ${message.kind === 'error' ? 'text-[var(--coral)]' : 'text-[var(--text-sec)]'}`} role={message.kind === 'error' ? 'alert' : 'status'}>
              {message.text}
            </p>
          )}
          <button
            type="button"
            onClick={() => void save()}
            disabled={saving}
            className="inline-flex items-center px-4 py-2 text-sm font-medium bg-[var(--slate)] text-white hover:opacity-90 disabled:opacity-50"
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </section>
      )}
    </div>
  );
}
